/* import __COLOCATED_TEMPLATE__ from './brand-signup-direct-hire-form.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, getProperties } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { array, bool, number, object, string } from 'yup';
import { trackEventAsync } from 'sh-www/utils/datalayer';
import { validateSchema } from 'sh-www/utils/validators';
import { KEY_DATES_OPTIONS } from 'sh-www/constants/routes/posting-create';
import { scrollToError } from 'sh-www/utils/handles-errors-v2';

const CLASS_NAME = 'GLM-brand-signup-direct-hire-form';

class BrandSignupDirectHireForm extends Component {
  @service('commands/stashed-data-commands') stashedDataCommandService;
  @service('error-tracker') errorTracker;

  @tracked title = '';
  @tracked services = [];
  @tracked location = null;
  @tracked locationInputValue = '';
  @tracked project_remote = false;
  @tracked assignment_deliverables = [];
  @tracked key_dates = null;
  @tracked schedule = [];
  @tracked message = '';
  @tracked compensation = '';
  @tracked estimated_days = '';

  @tracked errors = { };

  get className() {
    return CLASS_NAME;
  }

  get sellerName() {
    return this.args.freelancer.formalName;
  }

  get sellerFirstName() {
    return this.args.freelancer.firstName;
  }

  get availableServices() {
    return this.args.freelancer.services ?? [];
  }

  get minDayRate() {
    return this.args.freelancer.profile.minRate;
  }

  get keyDatesDropdownOptions() {
    return KEY_DATES_OPTIONS;
  }

  get hasErrors() {
    return Object.values(this.errors).some(Boolean);
  }

  get scheduleErrors() {
    return Object.entries(this.errors).reduce((accum, [errorKey, errorValues]) => {
      if (errorKey.startsWith('schedule') && errorValues) {
        const index = errorKey.split('.')[1];

        // 'schedule' handled below input
        if (index === undefined) {
          return accum;
        }

        // yup or server error
        const errorString = Array.isArray(errorValues)
          ? errorValues.join('. ')
          : errorValues;

        const errorAtIndex = accum[index];

        const combinedErrorString = errorAtIndex
          ? `${errorAtIndex}. ${errorString}`
          : errorString;

        // ensure correct punctuation
        accum[index] = combinedErrorString.replace(/\.{2,}/g, '.');
      }

      return accum;
    }, {});
  }

  get submitDisabled() {
    const hasLocation = Boolean(this.location || this.project_remote);

    return Boolean(
      this.handleSubmit.isRunning
      || this.title.length < 1
      || this.services.length < 1
      || !hasLocation
      || this.assignment_deliverables.length < 1
      || this.key_dates === null
      || this.message.length < 1
      || this.compensation.length < 1
      || this.estimated_days.length < 1
      || this.hasErrors
    );
  }

  get shouldSubmitShine() {
    return !this.submitDisabled;
  }

  /**
   * @param {Location} location
   */
  @action
  handleLocationChange(location) {
    this.location = location;
    this.locationInputValue = location?.formattedAddress ?? '';

    if (this.errors.location) {
      this.errors = { ...this.errors, location: undefined };
    }
  }

  /**
   * @param {string} value
   */
  @action
  handleLocationInput(value) {
    this.locationInputValue = value;
  }

  @action
  setFormField(field, value) {
    this[field] = value;

    const errors = { ...this.errors, [field]: undefined };

    // clear errors and any related ones too
    this.errors = Object.entries(errors).reduce((accum, [errorKey, errorValue]) => {
      const isScheduleArrayError = errorKey.startsWith('schedule');

      if (
        // toggling 'Key dates' off should clear related errors
        field === 'key_dates'
        && !value
        && isScheduleArrayError
      ) {
        return accum;
      } else if (
        // modifying field should clear related errors
        field === 'schedule'
        && isScheduleArrayError
      ) {
        return accum;
      }

      accum[errorKey] = errorValue;

      return accum;
    }, {});
  }

  @action
  handleServiceSelected(serviceOption) {
    if (!this.services.includes(service.id)) {
      this.services = [...this.services, serviceOption];

      if (this.errors.services) {
        this.errors = { ...this.errors, services: undefined };
      }
    }
  }

  @action
  handleServiceRemoved(serviceOption) {
    this.services = this.services.filter(({ id }) => id !== serviceOption.id);
  }

  @action
  toggleProjectRemote() {
    this.project_remote = !this.project_remote;
    this.location = null;
    this.locationInputValue = '';
  }

  @action
  validateData() {
    const schema = object({
      title: string()
        .min(5, 'Project title must be at least 5 characters')
        .required('Please enter a project title'),

      services: array()
        .min(1, 'Please select at least one service')
        .required('Please select at least one service'),

      location: object()
        .when('project_remote', {
          is: false,
          then: (schema) => schema.required('You must select a location'),
          otherwise: (schema) => schema.nullable(),
        }),

      project_remote: bool().optional(),

      assignment_deliverables: array()
        .min(1, 'Please enter at least one deliverable')
        .required('Please enter at least one deliverable'),

      key_dates: bool().required('Please select yes or no'),

      schedule: array()
        .when('key_dates', {
          is: true,
          then: (schema) => schema.min(1, 'Please enter at least one key date')
        }),

      message: string()
        .min(5, 'Your message must be at least 5 characters')
        .required('Please include a message'),

      compensation: number()
        .min(this.minDayRate, `${this.sellerFirstName} requires at least $${this.minDayRate} per day`)
        .required('Please enter a day rate'),

      estimated_days: number()
        .min(1, 'Minimum one day required')
        .required('Please enter an estimated number of days'),
    });

    const [values, validationErrors] = validateSchema(
      getProperties(this, Object.keys(schema.fields)),
      schema,
    );

    if (validationErrors) {
      return [null, validationErrors];
    }

    const jsonData = {
      ...values,
      seller_id: this.args.freelancer.id,
      schedule: values.key_dates ? values.schedule : [],
    };

    if (!values.project_remote) {
      jsonData['location_id'] = values.location.id;
    }

    delete jsonData.location;

    jsonData['assignment_deliverables'] = values.assignment_deliverables
      .map((item) => item.description)
      .filter((description) => description !== '');

    jsonData['services'] = values.services
      .map(item => Number.parseInt(item.id));

    return [jsonData];
  }

  @task
  *handleSubmit(e) {
    e.preventDefault();

    const [jsonData, errors] = this.validateData();

    if (errors) {
      this.errors = errors;
      scrollToError(this, errors);
      return;
    }

    try {
      const response = yield this.stashedDataCommandService.createStashedDirectAssignmentDraft(jsonData);
      sessionStorage.setItem('directAssignmentDraftId', response.uuid);
    } catch (error) {
      const responseErrors = Object.keys(error.errors).reduce((accum, errorKey) => {
        const [baseErrorKey, index, currentElementName] = errorKey.split('.');

        if (errorKey === 'generic-exception') {
          this.errorTracker.error('createStashedDirectAssignmentDraft failed', this, error);
          accum[errorKey] = 'Failed to save your project information. Please refresh and try again or contact support.';
        } else {
          accum[errorKey] = error.errors[errorKey][0];
        }

        return accum;
      }, {});

      this.errors = responseErrors;
      scrollToError(this, responseErrors);

      return;
    }

    yield trackEventAsync(this, 'form_submit', {
      form_id: 'direct_hire_form',
      form_name: 'direct_hire_form',
      form_destination: '/stashed-data/direct-assignment-draft',
      form_submit_text: 'Continue',
    });

    this.args.onComplete();
  }
}

export default BrandSignupDirectHireForm;
